import { availabilityFrom, availabilityText } from "../plans";
import type { LandingPage } from "./types";

export const landingPages: LandingPage[] = [
  {
    slug: "/replace-powershell-file-transfer-scripts",
    seoTitle: "Replace PowerShell File Transfer Scripts | FileAgent",
    seoDescription:
      "Retire fragile PowerShell file transfer scripts. FileAgent automates scheduled transfers across SMB, SFTP, FTP, S3, and Azure Blob with a web UI, logs, alerts, and central credentials.",
    eyebrow: "PowerShell script alternative",
    h1: "Replace fragile PowerShell file transfer scripts",
    intro:
      "PowerShell is great for one-off jobs. As a foundation for business-critical, recurring file transfers, it quietly becomes a liability — undocumented, owned by one person, and invisible until it fails. FileAgent moves those transfers into a managed web platform with scheduling, logs, alerts, and centralized credentials, so a transfer is something your whole team can see and operate.",
    heroBullets: ["No scripting required", "Central logs & alerts on every run", "Self-hosted on a server you control"],
    problem: {
      heading: "When a script becomes business-critical, it becomes a risk",
      body:
        "A PowerShell transfer script usually starts as a quick fix and ends up running payroll exports, EHR feeds, or nightly backups. The problem isn't PowerShell — it's that the script has no operator. There's no shared view of whether last night's run succeeded, no alert when it didn't, and no one but the author who fully understands it.",
      points: [
        "The script lives on one admin's machine or a forgotten server",
        "Credentials are hardcoded in the file or a plaintext config",
        "Failures are silent until a downstream team complains",
        "Editing it safely requires the original author — who may be gone",
        "There's no audit trail of what moved, when, or whether it worked",
      ],
    },
    broken: {
      heading: "The “current way” with PowerShell",
      intro: "A typical PowerShell transfer setup spreads the same job across four places no one looks at together:",
      items: [
        { label: "The .ps1 file", pain: "Custom logic for connecting, listing, copying, and cleaning up — readable only to its author." },
        { label: "Task Scheduler", pain: "The trigger lives separately from the logic, with its own opaque history and ‘last result’ codes." },
        { label: "A config / .env / hardcoded secret", pain: "SFTP keys and passwords sit in plaintext next to the script." },
        { label: "A log file (maybe)", pain: "If logging exists at all, it's a text file on the host that nobody monitors." },
      ],
    },
    solution: {
      heading: "FileAgent: the same transfers, but operable",
      body:
        "FileAgent gives each transfer a definition, a schedule, and a record — in one web UI. You rebuild the script's intent as a job (source, destination, mode, filters, retries) without writing or maintaining code, and every run is logged and alertable.",
      points: [
        "Define source → destination → operation (copy, move, or Sync/Mirror) in the UI",
        "Schedule it with the built-in scheduler instead of a separate Task Scheduler entry",
        "Store SFTP/SMB/cloud credentials once, encrypted and reusable across jobs",
        "Get email (or Slack/Teams) alerts the moment a run fails",
        "Keep run history with file counts, sizes, durations, and per-file detail",
      ],
    },
    workflow: {
      heading: "Example: a nightly export script, rebuilt as a job",
      intro: "An export that a PowerShell script used to push to a partner SFTP server becomes a five-minute setup:",
      steps: [
        { title: "Add the credential", body: "Enter the partner's SFTP host, user, and key once under Credentials, and test the connection before saving." },
        { title: "Create the job", body: "Source = the local export folder, destination = the SFTP path, mode = move, with a filename filter for the export pattern." },
        { title: "Set retries & schedule", body: "Turn on retries for flaky connections and schedule it for 02:00 — the next-run time is visible on the job." },
        { title: "Add a failure alert", body: "Point the failure alert at your team inbox so a missed export pages someone instead of surfacing as a complaint." },
      ],
    },
    shotCaption: "FileAgent job editor — source, destination, transfer mode, schedule, and retry settings in one screen.",
    benefits: {
      heading: "What you gain by retiring the script",
      items: [
        { title: "Visibility", body: "One dashboard shows every job's last run and next run — no more guessing whether the transfer happened." },
        { title: "Continuity", body: "Jobs are operated from the UI, so they survive the author leaving. Anyone with access can read, run, or edit them." },
        { title: "Faster troubleshooting", body: "When something fails, the run detail shows the exact error and the files involved — not a generic exit code." },
        { title: "Credential hygiene", body: "Secrets move out of script files into a central, encrypted store you can rotate without touching jobs." },
      ],
    },
    features: {
      heading: "Built for the transfers scripts struggle with",
      items: [
        { title: "Advanced scheduling", body: "Recurring runs with clear next-run times — no separate Task Scheduler to manage." },
        { title: "Retry, conflict & timeout controls", body: "Absorb transient SFTP/SMB failures and decide how to handle existing files." },
        { title: "Broad connectors", body: "Local, SMB, FTP, SFTP, AWS S3, and Azure Blob — same workflow for all of them." },
        { title: "Run history & alerts", body: "Per-run logs plus email, Slack, and Teams alerts so failures don't stay silent." },
        { title: "Scheduled Purge cleanup", body: `Replace the cleanup logic in your scripts with age- and pattern-based retention purges (${availabilityFrom("jobs.scheduledPurge")}). Deletes files permanently — it's not a backup.` },
      ],
    },
    faq: [
      { q: "Do I need to rewrite my PowerShell scripts?", a: "No. You recreate each transfer's intent as a FileAgent job using the web UI — source, destination, mode, schedule, and filters. There's no scripting to write or maintain." },
      { q: "Can FileAgent do everything my script does?", a: "FileAgent covers the common building blocks of transfer scripts: connecting to local/SMB/FTP/SFTP/S3/Azure, filtering files, copying, moving, and Sync/Mirror replication, retries, scheduling, and cleanup. Highly custom in-script logic may still warrant a hybrid approach." },
      { q: "Is FileAgent self-hosted?", a: "Yes. FileAgent installs on a Windows or Linux host you control, so your files move directly between your systems and aren't routed through a third-party cloud." },
      { q: "Where do credentials go?", a: "Into FileAgent's central credential store — encrypted and reused across jobs — instead of being hardcoded in script files or config." },
    ],
    related: [
      { label: "FileAgent vs. PowerShell + Scheduled Tasks", href: "/compare/fileagent-vs-powershell-scheduled-tasks" },
      { label: "Scheduled SFTP transfers", href: "/scheduled-sftp-transfers" },
      { label: "File transfer automation for IT teams", href: "/file-transfer-automation-for-it-teams" },
    ],
  },

  {
    slug: "/scheduled-sftp-transfers",
    seoTitle: "Scheduled SFTP Transfers, Automated | FileAgent",
    seoDescription:
      "Automate scheduled SFTP transfers with retries, alerts, and full run history. FileAgent replaces brittle SFTP scripts and Scheduled Tasks with a self-hosted web platform.",
    eyebrow: "Scheduled SFTP automation",
    h1: "Scheduled SFTP transfers that don't fail silently",
    intro:
      "Most recurring SFTP workflows — partner exchanges, vendor feeds, nightly exports — run on a script plus a scheduled task, with no one watching. FileAgent turns scheduled SFTP into a managed job: pick the schedule, store the credentials once, add retries, and get alerted the moment a transfer fails.",
    heroBullets: ["Recurring SFTP on a schedule", "Automatic retries for flaky links", "Failure alerts + full run history"],
    problem: {
      heading: "Recurring SFTP is where silent failures hide",
      body:
        "SFTP endpoints drop connections, rotate credentials, change IP allowlists, and go down for maintenance. A scheduled script handles none of that gracefully — it either errors into a log nobody reads or appears to succeed while moving nothing. By the time a partner asks where their file is, hours have passed.",
      points: [
        "A momentary network blip fails the whole run with no retry",
        "A rotated key or expired password breaks the job quietly",
        "‘Success’ with zero files looks fine but means the feed stopped upstream",
        "There's no shared place to confirm last night's exchange actually happened",
      ],
    },
    broken: {
      heading: "The “current way” for scheduled SFTP",
      intro: "Teams usually stitch scheduled SFTP together from tools that don't talk to each other:",
      items: [
        { label: "WinSCP / curl / sftp script", pain: "Hand-written transfer logic that has to re-implement retries and error handling itself." },
        { label: "Windows Task Scheduler / cron", pain: "Fires the script on a timer, but its run history and the transfer's history live in different places." },
        { label: "A stored key or password", pain: "Sits with the script; rotating it means editing files and hoping nothing else breaks." },
        { label: "Email-by-hope", pain: "No alerting, so failures are discovered by the recipient, not the operator." },
      ],
    },
    solution: {
      heading: "FileAgent: scheduled SFTP with a safety net",
      body:
        "FileAgent runs SFTP transfers on a real scheduler with retries, conflict handling, and timeouts built in — and records every run. You see the next run time, get alerted on failure, and can open any run to see exactly which files moved.",
      points: [
        "Advanced scheduling for weekday, time-of-day, and recurring patterns",
        "Retries with backoff so a dropped connection doesn't fail the night",
        "Conflict and timeout controls for large files and slow partners",
        "Email, Slack, and Teams alerts on failure (and optionally on success)",
        "Per-run history: files, sizes, durations, and the full error on any failure",
      ],
    },
    workflow: {
      heading: "Example: a nightly partner SFTP exchange",
      intro: "A vendor feed that has to land on a partner's SFTP server every weeknight at 1:00 AM:",
      steps: [
        { title: "Store the SFTP credential", body: "Add the partner host, port, and key under Credentials; click Test connection to confirm reachability and the IP allowlist." },
        { title: "Build the job", body: "Source = the export folder, destination = the partner's inbound path, mode = copy, filtered to the feed's filename pattern." },
        { title: "Schedule + retries", body: "Set it for 01:00 on weekdays and enable retries so a transient drop reattempts automatically." },
        { title: "Alert on failure", body: "If the run can't complete after retries, your team gets an email/Slack/Teams alert with the error — before the partner notices." },
      ],
    },
    shotCaption: "Run history for a scheduled SFTP job — status, files transferred, duration, and next run time.",
    benefits: {
      heading: "Why teams move scheduled SFTP to FileAgent",
      items: [
        { title: "No more silent misses", body: "Retries handle the blips; alerts surface the real failures so a missed exchange is caught in minutes, not hours." },
        { title: "One place to confirm", body: "The dashboard answers ‘did the SFTP run last night?' without logging into a server." },
        { title: "Easier partner onboarding", body: "Adding a new SFTP partner is a credential plus a job — not another bespoke script to maintain." },
        { title: "Auditable exchanges", body: "Run history gives you a record of every exchange for reviews and troubleshooting." },
      ],
    },
    features: {
      heading: "SFTP automation features",
      items: [
        { title: "SFTP & FTP connectors", body: "Connect modern SFTP and legacy FTP endpoints with stored, testable credentials." },
        { title: "Reliability controls", body: "Retries, conflict handling, and timeouts tuned per job for unreliable partners." },
        { title: "Copy, move & Sync/Mirror", body: `Pick the right operation — including Sync/Mirror on ${availabilityFrom("jobs.syncMirror")}.` },
        { title: "Alerting", body: `Email failure alerts on ${availabilityText("alerts.emailFailure")}; Slack and Teams on ${availabilityFrom("alerts.slack")}.` },
      ],
    },
    faq: [
      { q: "Can FileAgent schedule SFTP transfers automatically?", a: "Yes. Each SFTP job has its own schedule managed by FileAgent's built-in scheduler service — no separate Task Scheduler or cron entry to maintain. The next run time is shown right on the job." },
      { q: "What happens if an SFTP transfer fails?", a: "FileAgent retries transient failures based on your settings, and if the run still can't complete it marks the run failed and sends a failure alert with the underlying error. The run detail shows exactly which files were affected." },
      { q: "Does it support SFTP keys and passwords?", a: "Yes. SFTP credentials are stored centrally and encrypted, can be tested before use, and are reused across jobs so rotating them is a single update." },
      { q: "Which plans include SFTP and advanced scheduling?", a: `FTP/SFTP connectors are available on ${availabilityFrom("connector.sftp")}, and advanced scheduling on ${availabilityFrom("jobs.advancedScheduling")}. See the pricing page for the full matrix.` },
    ],
    related: [
      { label: "Replace PowerShell transfer scripts", href: "/replace-powershell-file-transfer-scripts" },
      { label: "SFTP to Azure Blob automation", href: "/sftp-to-azure-blob-automation" },
      { label: "FileAgent vs. WinSCP scripts", href: "/compare/fileagent-vs-winscp-scripts" },
    ],
  },

  {
    slug: "/automated-smb-to-s3-transfer",
    seoTitle: "Automated SMB to S3 File Transfer | FileAgent",
    seoDescription:
      "Automatically move files from SMB/network shares to AWS S3 on a schedule. FileAgent handles SMB-to-S3 transfers with retries, logs, alerts, and central credentials — self-hosted.",
    eyebrow: "SMB → AWS S3 automation",
    h1: "Automated SMB to S3 file transfers, on a schedule",
    intro:
      "Getting files off a Windows file share and into AWS S3 — for analytics, archival, or a cloud app — usually means a manual upload or a custom script with the AWS CLI. FileAgent automates SMB-to-S3 as a scheduled job with retries, logging, and alerts, so the data lands in your bucket reliably without anyone babysitting it.",
    heroBullets: ["SMB share → S3 bucket on a schedule", "Stored AWS + SMB credentials", "Retries, logs, and alerts"],
    problem: {
      heading: "Moving share data to the cloud shouldn't be a manual chore",
      body:
        "Network shares are where business files live; S3 is where the cloud needs them. Bridging the two by hand — or with a fragile AWS CLI script on someone's workstation — doesn't scale and doesn't get monitored. Files arrive late, partially, or not at all, and no one knows until a report is empty.",
      points: [
        "Manual uploads depend on a person remembering to do them",
        "AWS CLI scripts hardcode keys and re-implement retry logic poorly",
        "Large folders time out or partially upload with no clean recovery",
        "There's no record of which files made it to the bucket and when",
      ],
    },
    broken: {
      heading: "The “current way” for SMB-to-S3",
      intro: "The do-it-yourself approach tends to look like one of these — all hard to operate:",
      items: [
        { label: "Manual drag-and-drop", pain: "Someone uploads files through the AWS console or a tool when they remember." },
        { label: "An AWS CLI batch/PowerShell script", pain: "Hardcoded access keys, custom sync logic, and a scheduled task with no monitoring." },
        { label: "A desktop S3 client", pain: "Interactive and manual — fine for ad hoc, unworkable for a recurring business feed." },
        { label: "No audit trail", pain: "No central record of which files reached S3, so reconciliation is guesswork." },
      ],
    },
    solution: {
      heading: "FileAgent: SMB-to-S3 as a managed job",
      body:
        "FileAgent connects to both your SMB share and your S3 bucket, then moves or copies files between them on the schedule you set. AWS access keys live in the credential store, transfers retry on failure, and every run is logged with file counts and sizes.",
      points: [
        "Read from any reachable SMB/UNC path your FileAgent host can access",
        "Write to an S3 bucket and prefix using stored, scoped AWS credentials",
        "Copy or move (with optional archive of what was moved)",
        "Schedule it nightly, hourly, or on your business pattern",
        "Track every run, and get alerted if an upload fails",
      ],
    },
    workflow: {
      heading: "Example: nightly share archive to S3",
      intro: "Archiving a daily output folder from a file server into an S3 bucket every night:",
      steps: [
        { title: "Add both credentials", body: "Store the SMB service account and a scoped AWS access key (bucket + prefix) under Credentials, and test each." },
        { title: "Create the job", body: "Source = the SMB output folder, destination = the S3 bucket/prefix, mode = move with archiving enabled." },
        { title: "Schedule + retries", body: "Run it at 23:30 nightly with retries so a slow network or large file doesn't fail the job outright." },
        { title: "Monitor & alert", body: "Watch file counts and sizes per run on the dashboard, and get alerted if a night's archive doesn't complete." },
      ],
    },
    shotCaption: "An SMB-to-S3 job — SMB source path, S3 destination bucket/prefix, transfer mode, and schedule.",
    benefits: {
      heading: "Why automate SMB-to-S3 with FileAgent",
      items: [
        { title: "Reliable cloud landing", body: "Retries and scheduling mean files reach the bucket consistently, not just when someone uploads them." },
        { title: "Scoped, central AWS keys", body: "Use a least-privilege key for one bucket/prefix, stored once — not pasted into scripts on multiple machines." },
        { title: "Reconciliation built in", body: "Per-run file counts and sizes let you confirm exactly what landed in S3 each cycle." },
        { title: "Two-way capable", body: "Need S3 → share instead? The same job model runs in either direction." },
      ],
    },
    features: {
      heading: "Features for cloud transfers",
      items: [
        { title: "AWS S3 & Azure Blob connectors", body: "Move data to and from cloud object storage with stored credentials." },
        { title: "SMB / network share support", body: "Read and write Windows shares and NAS devices by UNC path." },
        { title: "Archive & restore", body: "Keep a recoverable copy of moved files and restore on demand." },
        { title: "Retry & timeout controls", body: "Handle large folders and slow links without partial, unrecoverable runs." },
      ],
    },
    faq: [
      { q: "Can FileAgent move files from an SMB share to S3 automatically?", a: "Yes. You create a job with the SMB share as the source and an S3 bucket/prefix as the destination, then schedule it. FileAgent handles the transfer, retries, and logging on every run." },
      { q: "How are AWS credentials handled?", a: "AWS access keys are stored in FileAgent's encrypted credential store and reused across jobs. Use a least-privilege IAM key scoped to the specific bucket and prefix the job needs." },
      { q: "Which plans include S3?", a: `AWS S3 and Azure Blob connectors are available on ${availabilityFrom("connector.s3")}. SMB and local transfers are available on ${availabilityText("connector.smb")}.` },
      { q: "Can it go from S3 back to a share?", a: "Yes. Sources and destinations are interchangeable, so the same model handles S3 → SMB, share → bucket, or bucket → bucket." },
    ],
    related: [
      { label: "SFTP to Azure Blob automation", href: "/sftp-to-azure-blob-automation" },
      { label: "Self-hosted managed file transfer", href: "/self-hosted-managed-file-transfer" },
      { label: "File transfer automation for IT teams", href: "/file-transfer-automation-for-it-teams" },
    ],
  },

  {
    slug: "/sftp-to-azure-blob-automation",
    seoTitle: "SFTP to Azure Blob Automation | FileAgent",
    seoDescription:
      "Automate SFTP to Azure Blob Storage transfers on a schedule. FileAgent pulls files from SFTP and lands them in Azure Blob with retries, logs, alerts, and central credentials.",
    eyebrow: "SFTP → Azure Blob automation",
    h1: "Automate SFTP to Azure Blob Storage transfers",
    intro:
      "When a partner or vendor drops files on an SFTP server and they need to land in Azure Blob Storage, the gap is usually filled by a person or a brittle script. FileAgent automates SFTP-to-Azure as a scheduled job — pull from SFTP, push to a Blob container — with retries, logging, and alerts so the pipeline runs itself.",
    heroBullets: ["SFTP source → Azure Blob container", "Scheduled, with retries", "Stored credentials + run history"],
    problem: {
      heading: "The SFTP-to-cloud hand-off is fragile and unmonitored",
      body:
        "SFTP is how partners deliver files; Azure Blob is where your cloud workloads expect them. Bridging the two with a scheduled script means re-implementing connection handling, retries, and error reporting yourself — and still ending up with no shared visibility into whether today's files made it.",
      points: [
        "Partner SFTP endpoints are flaky, allowlisted, and occasionally down",
        "A custom script puts Azure connection strings in plaintext config",
        "Partial or failed transfers leave the Blob container in an unknown state",
        "No central record means reconciling delivered vs. processed files is manual",
      ],
    },
    broken: {
      heading: "The “current way” for SFTP-to-Azure",
      intro: "Most teams cobble this together from disconnected parts:",
      items: [
        { label: "An SFTP pull script", pain: "Downloads files locally, with its own retry and error handling to maintain." },
        { label: "AzCopy / SDK upload step", pain: "A second script or step pushes to Blob, with the connection string stored in the clear." },
        { label: "A scheduled task", pain: "Chains the two steps on a timer, with no unified history of the end-to-end run." },
        { label: "Manual checks", pain: "Someone spot-checks the container to confirm files arrived — when they remember." },
      ],
    },
    solution: {
      heading: "FileAgent: one job, SFTP to Blob",
      body:
        "FileAgent connects to the SFTP source and the Azure Blob destination and moves files between them on a schedule, as a single managed job. Credentials are stored and encrypted, transfers retry automatically, and every run is recorded and alertable.",
      points: [
        "Pull from an SFTP path and write directly to an Azure Blob container",
        "Store SFTP keys and Azure Blob credentials once, encrypted",
        "Move or copy, with conflict handling for files that already exist",
        "Schedule on your cadence with retries for unreliable partners",
        "See per-run file counts and get alerted on failure",
      ],
    },
    workflow: {
      heading: "Example: hourly vendor drop into Blob",
      intro: "A vendor posts files to SFTP throughout the day that need to reach an Azure Blob container hourly:",
      steps: [
        { title: "Store both credentials", body: "Add the vendor's SFTP credential and the Azure Blob credential under Credentials, and test both connections." },
        { title: "Create the job", body: "Source = the SFTP inbound folder, destination = the Blob container/path, mode = move so files aren't re-processed." },
        { title: "Schedule hourly", body: "Run it every hour with retries, so a brief SFTP outage simply reattempts on the next pass or retry." },
        { title: "Alert + audit", body: "Failure alerts notify your team, and run history gives you an hour-by-hour record of what was delivered." },
      ],
    },
    shotCaption: "An SFTP-to-Azure Blob job — SFTP source, Blob container destination, mode, schedule, and retries.",
    benefits: {
      heading: "Why run SFTP-to-Azure on FileAgent",
      items: [
        { title: "End-to-end in one job", body: "No two-step script chain — the pull and the push are a single, logged run." },
        { title: "Resilient to partner outages", body: "Retries and scheduling smooth over the inevitable SFTP hiccups." },
        { title: "Credentials out of config", body: "SFTP keys and Azure connection details live in the encrypted store, not in plaintext files." },
        { title: "Clear delivery record", body: "Run history makes ‘what did we receive and land today?' a one-screen answer." },
      ],
    },
    features: {
      heading: "Features for SFTP-to-cloud pipelines",
      items: [
        { title: "SFTP + Azure Blob connectors", body: "Connect partner SFTP and Azure Blob containers with stored, testable credentials." },
        { title: "Advanced scheduling", body: "Hourly, daily, or business-calendar patterns with visible next-run times." },
        { title: "Reliability controls", body: "Retries, conflict handling, and timeouts for unpredictable partner endpoints." },
        { title: "Alerts & history", body: "Email/Slack/Teams alerts plus a per-run record of every delivery." },
      ],
    },
    faq: [
      { q: "Can FileAgent move files from SFTP to Azure Blob?", a: "Yes. A single job uses the SFTP server as the source and an Azure Blob container as the destination, runs on your schedule, and handles retries and logging automatically." },
      { q: "Do I need separate scripts for the download and upload?", a: "No. FileAgent performs the SFTP-to-Blob transfer as one managed job, so there's no two-step script chain to maintain or monitor separately." },
      { q: "How are Azure credentials stored?", a: "Azure Blob connection details are kept in FileAgent's encrypted credential store and reused across jobs, rather than living in plaintext configuration." },
      { q: "Which plans include SFTP and Azure Blob?", a: `FTP/SFTP is available on ${availabilityFrom("connector.sftp")}; AWS S3 and Azure Blob are available on ${availabilityFrom("connector.s3")}. See pricing for the full matrix.` },
    ],
    related: [
      { label: "Automated SMB to S3 transfer", href: "/automated-smb-to-s3-transfer" },
      { label: "Scheduled SFTP transfers", href: "/scheduled-sftp-transfers" },
      { label: "Self-hosted managed file transfer", href: "/self-hosted-managed-file-transfer" },
    ],
  },

  {
    slug: "/self-hosted-managed-file-transfer",
    seoTitle: "Self-Hosted Managed File Transfer (MFT) | FileAgent",
    seoDescription:
      "FileAgent is self-hosted managed file transfer for businesses that want control and auditability without enterprise MFT cost or complexity. Schedule, monitor, and audit transfers from your own server.",
    eyebrow: "Self-hosted MFT",
    h1: "Self-hosted managed file transfer, without the enterprise overhead",
    intro:
      "Managed file transfer used to mean a heavy enterprise platform with a price tag and a project plan to match. FileAgent delivers the parts teams actually need — scheduling, central credentials, logging, alerts, and access control — as a self-hosted application you can stand up on one server and operate from a web UI.",
    heroBullets: ["Runs on your own Windows/Linux host", "Operational audit trail on every run", "No enterprise MFT price tag"],
    problem: {
      heading: "Most teams are stuck between two bad options",
      body:
        "On one side: fragile scripts and Scheduled Tasks that are cheap but unmanageable. On the other: full enterprise MFT suites that are powerful but expensive, complex, and slow to deploy. The vast middle — SMBs, IT teams, and departments that just need reliable, auditable transfers — is underserved.",
      points: [
        "Scripts give you no visibility, no audit trail, and no central control",
        "Enterprise MFT gives you all of that — at a cost and complexity most teams can't justify",
        "Cloud sync tools control too little: limited scheduling, protocols, and auditability",
        "Sensitive data and on-prem systems make a self-hosted option the natural fit",
      ],
    },
    broken: {
      heading: "What ‘managed' usually costs you",
      intro: "The traditional paths to managed transfers each come with a tax:",
      items: [
        { label: "Roll-your-own scripts", pain: "Cheap up front, expensive forever — maintenance, key sprawl, and no audit trail." },
        { label: "Enterprise MFT suite", pain: "Licensing, professional services, and a steep operational learning curve." },
        { label: "Cloud sync service", pain: "Convenient, but you give up scheduling control, protocol coverage, and self-hosting." },
        { label: "SaaS transfer tools", pain: "Your files route through someone else's cloud — a non-starter for many on-prem or regulated workflows." },
      ],
    },
    solution: {
      heading: "FileAgent: the practical middle ground",
      body:
        "FileAgent is designed to be the right-sized MFT for teams that need control without complexity. It installs on a host you own, keeps your data inside your network, and gives you the management layer — jobs, schedules, credentials, logs, roles — through a clean web console.",
      points: [
        "Self-hosted on a server you control; files move directly between your systems",
        "Central credential store, encrypted and reused across jobs",
        "Run history with file-level detail for an operational audit trail",
        `Role-based access and job-scoped permissions on ${availabilityText("job_scoped_rbac")}`,
        "LDAP, IP allowlisting, and session controls for advanced teams",
      ],
    },
    workflow: {
      heading: "What ‘managed' looks like day to day",
      intro: "Once FileAgent is installed, managing transfers is a console, not a codebase:",
      steps: [
        { title: "Install on your host", body: "Install the Linux .deb or Windows EXE on a server that can reach your sources and destinations." },
        { title: "Add credentials & jobs", body: "Store connections once, then define jobs for each recurring transfer with the right mode and schedule." },
        { title: "Operate from the console", body: "Monitor the dashboard, review logs, manage users and roles, and adjust schedules — all in the browser." },
        { title: "Prove it ran", body: "Use run history and retention to show what moved, when, and whether it succeeded." },
      ],
    },
    shotCaption: "The FileAgent dashboard — job status, recent runs, and system health across every transfer.",
    benefits: {
      heading: "Why self-hosted MFT fits these teams",
      items: [
        { title: "Data stays in your network", body: "Transfers run on your host between your systems — nothing is routed through a vendor cloud." },
        { title: "Right-sized cost", body: "Pay for transfer automation, not a six-figure enterprise platform you'll use a fraction of." },
        { title: "Operational control", body: "Schedules, retries, roles, and retention are yours to set, on infrastructure you run." },
        { title: "Auditability", body: "Every run is recorded, giving operations and reviewers a clear, queryable history." },
      ],
    },
    features: {
      heading: "Management features that matter",
      items: [
        { title: "Centralized jobs & scheduling", body: "All transfers in one console with visible next-run times." },
        { title: "Credential management", body: "Encrypted, reusable connections separate from job definitions." },
        { title: "Roles & access control", body: `RBAC, job-scoped permissions, LDAP, and IP allowlisting on ${availabilityText("security.ldap")}.` },
        { title: "Logs, retention & backup", body: "Run history with configurable retention, plus configuration backup/restore." },
      ],
    },
    faq: [
      { q: "What does self-hosted managed file transfer mean?", a: "It means the transfer management platform runs on infrastructure you control — a Windows or Linux host on your network — rather than as a cloud service. Your files move directly between your systems, and you own the data path." },
      { q: "How is FileAgent different from enterprise MFT platforms?", a: "FileAgent focuses on the controls most teams need — scheduling, credentials, logging, alerts, roles, retention — without the licensing cost, deployment project, and operational complexity of a full enterprise MFT suite. It's the practical middle ground." },
      { q: "Does FileAgent provide an audit trail?", a: "Yes. Every run is recorded with status, timing, file counts, and per-file detail, retained according to your plan — an operational audit trail for what moved where and when. FileAgent does not claim formal compliance certifications." },
      { q: "What access controls are available?", a: `Multiple named users with shared administration on ${availabilityFrom("team.sharedAdmin")}; job-scoped RBAC, archive permissions, LDAP, IP allowlisting, and session security on ${availabilityText("job_scoped_rbac")}.` },
    ],
    related: [
      { label: "FileAgent vs. enterprise MFT", href: "/compare/fileagent-vs-enterprise-mft" },
      { label: "FileAgent vs. GoAnywhere for SMBs", href: "/compare/fileagent-vs-goanywhere-for-smbs" },
      { label: "File transfer automation for IT teams", href: "/file-transfer-automation-for-it-teams" },
    ],
  },

  {
    slug: "/file-transfer-automation-for-it-teams",
    seoTitle: "File Transfer Automation for IT Teams | FileAgent",
    seoDescription:
      "Give IT teams centralized control over file transfers. FileAgent replaces scattered scripts and Scheduled Tasks with a self-hosted web platform for jobs, credentials, logs, and schedules.",
    eyebrow: "For IT teams",
    h1: "File transfer automation built for IT teams",
    intro:
      "IT teams inherit file transfers from everywhere — a finance export here, a vendor feed there, a backup job no one documented. FileAgent gives the team one place to run all of it: a self-hosted web console for jobs, credentials, schedules, and logs, so transfers become a managed service instead of a pile of scripts and scheduled tasks.",
    heroBullets: ["One console for every transfer", "Shared visibility & roles", "Self-hosted, audit-ready"],
    problem: {
      heading: "Transfer sprawl is an IT team problem",
      body:
        "No single person owns ‘file transfers' — so they accumulate across servers, scripts, and scheduled tasks until the team can't say with confidence what runs, where, or whether it's working. Every incident becomes an archaeology project, and every staff change is a knowledge loss.",
      points: [
        "Jobs are spread across machines with no inventory",
        "Credentials are duplicated and hardcoded in multiple places",
        "Only one person understands each critical transfer",
        "Failures are reported by end users, not detected by the team",
        "There's no audit trail for security or compliance reviews",
      ],
    },
    broken: {
      heading: "The “current way” for most IT teams",
      intro: "Transfer automation is usually an accidental architecture:",
      items: [
        { label: "Scripts on N servers", pain: "PowerShell, batch, and bash files scattered across hosts with no central inventory." },
        { label: "Per-host schedulers", pain: "Task Scheduler and cron entries that each team member configured differently." },
        { label: "Shared spreadsheets / tribal knowledge", pain: "The closest thing to documentation is in someone's head." },
        { label: "Reactive monitoring", pain: "You find out a transfer broke when a user opens a ticket." },
      ],
    },
    solution: {
      heading: "FileAgent: transfers as a managed service",
      body:
        "FileAgent centralizes every transfer into one console the whole team operates. Jobs, credentials, schedules, and logs live in one place, with roles to control who can do what — so the team has shared ownership and a single source of truth.",
      points: [
        "A single inventory of every job and its schedule",
        "Central, encrypted credentials instead of copies in scripts",
        "Shared dashboard so the whole team sees status and failures",
        "Roles and job-scoped permissions to delegate safely",
        "Run history and retention for reviews and incident response",
      ],
    },
    workflow: {
      heading: "How IT teams roll FileAgent out",
      intro: "Consolidating transfer sprawl is incremental, not a big-bang migration:",
      steps: [
        { title: "Stand up one host", body: "Install FileAgent on a managed server that can reach your sources and destinations." },
        { title: "Migrate the riskiest jobs first", body: "Recreate the transfers that hurt most when they fail — the silent, single-owner ones — as FileAgent jobs." },
        { title: "Centralize credentials", body: "Move connection secrets out of scripts into the credential store and retire the duplicates." },
        { title: "Add the team", body: "Create accounts with appropriate roles so the whole team can see status and operate jobs." },
      ],
    },
    shotCaption: "The jobs list — every transfer, its mode, schedule, and last/next run, owned by the whole team.",
    benefits: {
      heading: "What IT teams get",
      items: [
        { title: "No more single points of knowledge", body: "Jobs are operated from the UI, so transfers survive turnover and time off." },
        { title: "Proactive failure detection", body: "Alerts catch problems before users do, cutting ticket volume and MTTR." },
        { title: "Security review-ready", body: "Central credentials, roles, and run history make access and activity easy to show." },
        { title: "Faster onboarding", body: "New team members learn one console instead of a dozen bespoke scripts." },
      ],
    },
    features: {
      heading: "Features IT teams rely on",
      items: [
        { title: "Centralized job management", body: "Every transfer in one place, with status and next-run at a glance." },
        { title: "FileAgent AI Assistant", body: `Create and edit jobs in plain English, get immediate product guidance, and receive workflow recommendations on ${availabilityText("assistant.ai")} using your own supported provider.` },
        { title: "RBAC & LDAP", body: `Role-based access, job-scoped permissions, and directory sign-in on ${availabilityText("security.ldap")}.` },
        { title: "Alerts & run history", body: "Proactive alerting plus a retained, per-run record of activity." },
        { title: "Backup & restore", body: "Back up FileAgent's configuration and restore onto a replacement host." },
      ],
    },
    faq: [
      { q: "How does FileAgent help an IT team specifically?", a: "It replaces scattered scripts and per-host scheduled tasks with one console where the whole team can see, run, and manage transfers — with shared credentials, roles, alerts, and run history. Transfers become a managed service instead of tribal knowledge." },
      { q: "Can the AI Assistant help new administrators?", a: `Yes. On ${availabilityText("assistant.ai")}, it can answer FileAgent questions, explain job types and settings, recommend workflow configurations, and prepare job changes for the user to review and confirm. Your organization supplies and manages a supported Claude, Gemini, or OpenAI API key.` },
      { q: "Can multiple admins manage transfers?", a: `Yes. ${availabilityFrom("team.sharedAdmin")} support multiple users with shared administration; ${availabilityText("job_scoped_rbac")} add job-scoped RBAC, LDAP sign-in, and IP/session controls.` },
      { q: "Do we have to migrate everything at once?", a: "No. Most teams start by moving their riskiest, single-owner transfers into FileAgent and consolidate the rest over time." },
      { q: "Is it self-hosted?", a: "Yes. FileAgent runs on a Windows or Linux host you control, keeping file movement inside your own network." },
    ],
    related: [
      { label: "Self-hosted managed file transfer", href: "/self-hosted-managed-file-transfer" },
      { label: "Replace PowerShell transfer scripts", href: "/replace-powershell-file-transfer-scripts" },
      { label: "FileAgent vs. PowerShell + Scheduled Tasks", href: "/compare/fileagent-vs-powershell-scheduled-tasks" },
    ],
  },

  {
    slug: "/robocopy-alternative-for-business",
    seoTitle: "Robocopy Alternative for Business | FileAgent",
    seoDescription:
      "Looking for a Robocopy alternative with scheduling, logging, alerts, and cloud/SFTP support? FileAgent automates business file transfers across SMB, SFTP, S3, and Azure with a web UI.",
    eyebrow: "Robocopy alternative",
    h1: "A Robocopy alternative built for business workflows",
    intro:
      "Robocopy is a dependable Windows tool for copying and mirroring files between local and SMB paths. But the moment a business needs scheduling, alerts, cloud or SFTP destinations, central credentials, and an audit trail, a Robocopy command wrapped in a scheduled task starts to creak. FileAgent keeps the reliability and adds the management layer.",
    heroBullets: ["Beyond local/SMB: SFTP, S3, Azure", "Scheduling, alerts & run history", "Web UI, not command lines"],
    problem: {
      heading: "Where a Robocopy + Task Scheduler setup runs out of road",
      body:
        "Robocopy excels at what it does, but it's a command-line copy engine, not a transfer platform. Businesses quickly hit its edges: it's Windows-only, it copies to local/SMB paths (not SFTP, S3, or Azure), its scheduling and alerting are whatever you bolt on, and its ‘audit trail' is a text log on the host.",
      points: [
        "No native SFTP, FTP, S3, or Azure Blob destinations",
        "Scheduling and alerting depend on Task Scheduler and custom glue",
        "Credentials and flags live in scripts, not a managed store",
        "Visibility is a log file per server, not a shared dashboard",
        "Mirror mistakes are unforgiving and hard to recover without a backup",
      ],
    },
    broken: {
      heading: "The “current way” with Robocopy",
      intro: "A typical business Robocopy setup is a small pile of moving parts:",
      items: [
        { label: "A Robocopy command with flags", pain: "/MIR, /Z, /R, /W tuned by trial and error and pasted into a .bat file." },
        { label: "Task Scheduler", pain: "Runs the batch file on a timer with cryptic last-result codes." },
        { label: "A log file", pain: "Robocopy's verbose output dumped to a file no one reviews until something breaks." },
        { label: "Hope for non-local destinations", pain: "Cloud or SFTP targets require a second, entirely different tool." },
      ],
    },
    solution: {
      heading: "FileAgent: keep the reliability, add the platform",
      body:
        "FileAgent gives you copy, move, and Sync/Mirror across far more than local and SMB — including SFTP, FTP, S3, and Azure Blob — managed from a web UI with scheduling, retries, alerts, and run history built in. The same dependable behavior, now operable by a team.",
      points: [
        "Copy, move, and Sync/Mirror across local, SMB, SFTP, FTP, S3, and Azure Blob",
        "Built-in scheduling with visible next-run times",
        "Email/Slack/Teams alerts instead of unread log files",
        "Central credentials and run history with retention",
        "Archive of moved files so mistakes are recoverable",
      ],
    },
    workflow: {
      heading: "Example: a Sync/Mirror job, modernized",
      intro: "Replacing a nightly Robocopy /MIR between two shares — and extending it to the cloud:",
      steps: [
        { title: "Recreate it as Sync/Mirror", body: "Source and destination shares with the operation set to Sync/Mirror, validated against a test destination first." },
        { title: "Schedule it", body: "Run nightly with retries; the next run time is visible without opening Task Scheduler." },
        { title: "Extend to cloud", body: "Add a second job that copies the same data to S3 or Azure for off-site retention — no new tool required." },
        { title: "Alert & audit", body: "Failure alerts replace the unread log, and run history shows exactly what changed each night." },
      ],
    },
    shotCaption: "A Sync/Mirror job in FileAgent — source, destination, operation, schedule, and run history.",
    benefits: {
      heading: "What you gain over Robocopy + scripts",
      items: [
        { title: "More destinations", body: "Reach SFTP, FTP, S3, and Azure Blob — not just local and SMB paths." },
        { title: "Real monitoring", body: "A shared dashboard and alerts instead of per-server text logs." },
        { title: "Safer Sync/Mirror", body: "Test against scratch destinations and recover moved files from the archive." },
        { title: "Team operability", body: "Manage from a web UI so transfers aren't locked to one admin's command line." },
      ],
    },
    features: {
      heading: "Features beyond a copy command",
      items: [
        { title: "Copy, move & Sync/Mirror", body: `The familiar behaviors, plus Sync/Mirror on ${availabilityFrom("jobs.syncMirror")}.` },
        { title: "Cloud & SFTP connectors", body: "S3, Azure Blob, SFTP, and FTP alongside local and SMB." },
        { title: "Scheduling & retries", body: "Recurring runs with reliability controls for large transfers." },
        { title: "Alerts, history & archive", body: "Proactive alerts, retained run history, and recoverable archives." },
      ],
    },
    faq: [
      { q: "Is FileAgent a drop-in replacement for Robocopy?", a: "For business workflows, it's a more capable alternative: it does copy, move, and Sync/Mirror replication like Robocopy, then adds scheduling, alerts, cloud and SFTP destinations, central credentials, and run history. For a quick local-to-local copy on a single machine, Robocopy is still perfectly good." },
      { q: "Can FileAgent mirror folders like Robocopy /MIR?", a: "Yes. FileAgent's Sync/Mirror operation makes the destination match the source, including removing files that no longer exist in the source. Always test a Sync/Mirror job against a non-production destination first, and FileAgent's archive helps you recover if needed." },
      { q: "Does FileAgent work with non-Windows destinations?", a: "Yes. Unlike Robocopy, FileAgent transfers to and from SFTP, FTP, AWS S3, and Azure Blob in addition to local and SMB paths, and runs on Windows or Linux hosts." },
      { q: "Do I still need Task Scheduler?", a: "No. FileAgent has its own scheduler, so jobs run on a schedule you manage in the web UI with visible next-run times." },
    ],
    related: [
      { label: "FileAgent vs. Robocopy", href: "/compare/fileagent-vs-robocopy" },
      { label: "Automated SMB to S3 transfer", href: "/automated-smb-to-s3-transfer" },
      { label: "File transfer automation for IT teams", href: "/file-transfer-automation-for-it-teams" },
    ],
  },

  {
    slug: "/winscp-script-alternative",
    seoTitle: "WinSCP Script Alternative | FileAgent",
    seoDescription:
      "A managed alternative to WinSCP scripts and scheduled tasks. FileAgent automates SFTP/FTP transfers with a web UI, central credentials, scheduling, retries, alerts, and run history.",
    eyebrow: "WinSCP script alternative",
    h1: "A managed alternative to WinSCP scripts",
    intro:
      "WinSCP is an excellent SFTP/FTP client, and its scripting can automate transfers. But once you're running WinSCP scripts on a schedule across several servers, you've built an unmanaged transfer system: logic in script files, secrets in config, triggers in Task Scheduler, and no shared visibility. FileAgent gives those same transfers a home with a web UI, central credentials, and monitoring.",
    heroBullets: ["No scripts to maintain", "Central, encrypted credentials", "Scheduling, alerts & run history"],
    problem: {
      heading: "WinSCP scripting scales into a maintenance problem",
      body:
        "One WinSCP script is fine. Ten of them — across a few servers, each with its own .txt script, credential, and scheduled task — is a system no one designed and no one fully owns. The transfers work until a host is rebuilt, a key rotates, or the person who wrote them moves on.",
      points: [
        "Transfer logic lives in WinSCP script files scattered across hosts",
        "Credentials sit in scripts or saved sessions, duplicated per machine",
        "Each script has its own Task Scheduler entry and its own log",
        "There's no single dashboard for ‘did all the transfers run?'",
        "Onboarding someone means walking them through bespoke scripts",
      ],
    },
    broken: {
      heading: "The “current way” with WinSCP scripts",
      intro: "An automated WinSCP setup is several disconnected pieces per job:",
      items: [
        { label: "A WinSCP script file", pain: "open/get/put/close commands plus error handling, maintained by hand per transfer." },
        { label: "A saved session or credential", pain: "Connection secrets stored per machine, hard to inventory or rotate centrally." },
        { label: "Task Scheduler", pain: "Triggers winscp.com on a timer, with history separate from the transfer's." },
        { label: "Per-job logs", pain: "WinSCP's log files on each host — useful for forensics, useless for monitoring." },
      ],
    },
    solution: {
      heading: "FileAgent: WinSCP-style transfers, centrally managed",
      body:
        "FileAgent replaces the script-plus-scheduler pattern with managed jobs. You define each SFTP/FTP transfer once in the web UI, store credentials centrally, and let FileAgent schedule, retry, log, and alert — so the transfers are visible and operable by the whole team.",
      points: [
        "Recreate each WinSCP transfer as a job — no scripting",
        "Store SFTP/FTP credentials once, encrypted and reusable",
        "Schedule with the built-in scheduler, not per-host Task Scheduler",
        "Retries and conflict handling instead of hand-written error logic",
        "Central run history and failure alerts across every transfer",
      ],
    },
    workflow: {
      heading: "Example: consolidating WinSCP scripts",
      intro: "Bringing a handful of scheduled WinSCP scripts under one roof:",
      steps: [
        { title: "Inventory the scripts", body: "List each WinSCP script's source, destination, schedule, and credential — usually the first time they're written down." },
        { title: "Recreate as jobs", body: "Build a FileAgent job per transfer with the same intent, choosing copy or move and the right filters." },
        { title: "Centralize credentials", body: "Store each endpoint's credential once and point the jobs at it, retiring the per-host copies." },
        { title: "Decommission the scripts", body: "Once jobs are running and alerting, remove the scripts and scheduled tasks they replaced." },
      ],
    },
    shotCaption: "FileAgent credentials list — SFTP/FTP connections stored once, testable, and reused across jobs.",
    benefits: {
      heading: "Why teams move off WinSCP scripts",
      items: [
        { title: "One system, not many scripts", body: "Every SFTP/FTP transfer lives in one console with shared visibility." },
        { title: "Central credential control", body: "Rotate a key in one place instead of hunting through scripts and saved sessions." },
        { title: "Monitoring, not forensics", body: "A dashboard and alerts replace log files you only read after a failure." },
        { title: "Survives host rebuilds", body: "Jobs and credentials live in FileAgent, not in files tied to a specific machine." },
      ],
    },
    features: {
      heading: "Features that replace the script stack",
      items: [
        { title: "SFTP & FTP connectors", body: "The protocols WinSCP scripts target, with stored, testable credentials." },
        { title: "Scheduling & retries", body: "Built-in scheduling with reliability controls — no Task Scheduler glue." },
        { title: "Alerts & run history", body: "Proactive alerts and a retained record of every transfer." },
        { title: "Roles & multi-user", body: "Shared administration so transfers aren't tied to one person's desktop." },
      ],
    },
    faq: [
      { q: "Is FileAgent a replacement for WinSCP?", a: "For interactive, ad hoc transfers, WinSCP's GUI is great and you may keep using it. For automated, scheduled, business-critical SFTP/FTP transfers, FileAgent replaces the script-and-scheduler pattern with managed jobs, central credentials, scheduling, alerts, and run history." },
      { q: "Do I have to rewrite my WinSCP scripts?", a: "No. You recreate each transfer's intent as a FileAgent job in the web UI — source, destination, mode, schedule, filters — without writing scripts. Once the jobs run and alert, you retire the scripts." },
      { q: "Can FileAgent connect to the same SFTP/FTP servers?", a: "Yes. FileAgent supports SFTP and FTP endpoints with stored credentials you can test before use, and reuses them across jobs." },
      { q: "How is credential rotation handled?", a: "Credentials live in FileAgent's central, encrypted store. Rotating a key or password is a single update that every job using it picks up — no editing scripts on multiple hosts." },
    ],
    related: [
      { label: "FileAgent vs. WinSCP scripts", href: "/compare/fileagent-vs-winscp-scripts" },
      { label: "Scheduled SFTP transfers", href: "/scheduled-sftp-transfers" },
      { label: "FileAgent vs. FileZilla", href: "/compare/fileagent-vs-filezilla" },
    ],
  },
];

export const landingBySlug: Record<string, LandingPage> = Object.fromEntries(
  landingPages.map((page) => [page.slug, page]),
);
